import { Link } from 'react-router-dom';

export default function TaktThinkingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <Link 
            to="/theory" 
            className="inline-flex items-center text-white/90 hover:text-white mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            理論一覧に戻る
          </Link>
          <h1 className="text-5xl font-bold mb-6">タクト思考（Takt Thinking）</h1>
          <p className="text-2xl text-white/95 font-medium leading-relaxed">
            なぜ同じ人数なのに、楽な時間と地獄の時間が生まれるのか
          </p>
          <p className="text-xl text-white/90 mt-4">
            ― 薬局は「患者の来るリズム」に合わせていない ―
          </p>
        </div>
      </section>

      {/* 結論 */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-10 shadow-xl">
            <div className="flex items-start">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                <i className="ri-lightbulb-flash-line text-3xl text-orange-500"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">結論</h2>
                <p className="text-xl text-white leading-relaxed">
                  調剤薬局が苦しくなる本当の理由は、仕事量が多いからではなく、<strong>"リズムが合っていない"</strong>からです。タクト思考は、需要の波に合わせて現場を整える視点です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 理論の解説 */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">理論の解説｜タクト思考とは何か</h2>
            
            <div className="bg-purple-50 border-l-4 border-purple-500 p-8 rounded-r-xl mb-8">
              <p className="text-lg text-gray-800 leading-relaxed mb-4">
                <strong className="text-purple-700">タクト（Takt）</strong>とは、「需要に合わせた、理想的な処理間隔」を意味します。
              </p>
              <p className="text-lg text-gray-800 leading-relaxed">
                もともとは製造業で使われてきた考え方ですが、本質はシンプルです。
              </p>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-8 rounded-xl mb-8 text-center">
              <p className="text-2xl font-bold">
                お客さんが来るペースに、仕事のペースを合わせる
              </p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              逆に言えば、このリズムがズレた瞬間に、
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <i className="ri-time-line text-4xl text-red-500 mb-3"></i>
                <p className="text-lg font-bold text-red-700">待ち時間</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 text-center">
                <i className="ri-emotion-unhappy-line text-4xl text-orange-500 mb-3"></i>
                <p className="text-lg font-bold text-orange-700">ストレス</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                <i className="ri-error-warning-line text-4xl text-yellow-600 mb-3"></i>
                <p className="text-lg font-bold text-yellow-700">ミス</p>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              が一気に増えます。
            </p>
          </div>
        </div>
      </section>

      {/* タクトの波 */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">調剤薬局に"タクトの波"は確実に存在する</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              薬局現場を思い浮かべてください。
            </p>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-48 flex-shrink-0">
                    <span className="text-lg font-bold text-purple-700">午前10〜11時：</span>
                  </div>
                  <div className="flex-1">
                    <div className="bg-red-500 text-white px-6 py-3 rounded-lg font-bold text-center">
                      外来ピーク
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-48 flex-shrink-0">
                    <span className="text-lg font-bold text-purple-700">午後14〜15時：</span>
                  </div>
                  <div className="flex-1">
                    <div className="bg-green-500 text-white px-6 py-3 rounded-lg font-bold text-center">
                      比較的落ち着く
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-48 flex-shrink-0">
                    <span className="text-lg font-bold text-purple-700">夕方：</span>
                  </div>
                  <div className="flex-1">
                    <div className="bg-orange-500 text-white px-6 py-3 rounded-lg font-bold text-center">
                      再び増える
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              これは偶然ではありません。処方箋の到着には、必ず時間帯ごとの"波"があります。
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl">
              <p className="text-lg text-gray-800 leading-relaxed mb-4">
                しかし多くの薬局では、
              </p>
              <ul className="space-y-2 text-lg text-gray-800">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>人員配置</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>タスクの割り振り</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span>重い処方の処理</span>
                </li>
              </ul>
              <p className="text-lg text-gray-800 leading-relaxed mt-4">
                が、この波と<strong>無関係に決められています</strong>。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* タクト思考がない薬局 */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">タクト思考がない薬局で起きること</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 午前（需要ピーク） */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-arrow-up-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-red-700">午前（需要ピーク）</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start text-gray-800">
                    <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>一包化・在宅分包が混ざる</span>
                  </li>
                  <li className="flex items-start text-gray-800">
                    <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>投薬が詰まる</span>
                  </li>
                  <li className="flex items-start text-gray-800">
                    <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>待ち時間増大</span>
                  </li>
                  <li className="flex items-start text-gray-800">
                    <i className="ri-arrow-right-s-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>判断力低下 → ミスリスク上昇</span>
                  </li>
                </ul>
              </div>

              {/* 午後（需要の谷） */}
              <div className="bg-gradient-to-br from-green-50 to-teal-50 border-2 border-green-300 rounded-xl p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-arrow-down-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-green-700">午後（需要の谷）</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start text-gray-800">
                    <i className="ri-arrow-right-s-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>比較的ヒマ</span>
                  </li>
                  <li className="flex items-start text-gray-800">
                    <i className="ri-arrow-right-s-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>でも集中力が落ちる</span>
                  </li>
                  <li className="flex items-start text-gray-800">
                    <i className="ri-arrow-right-s-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>単純ミスが増える</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-red-500 to-orange-500 text-white p-6 rounded-xl text-center">
              <p className="text-xl font-bold">
                👉 忙しさと安全が、どちらも崩れる。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 調剤薬局への翻訳 */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">調剤薬局への翻訳｜タクトをどう使うか</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              タクト思考では、「いつ、何をやるか」を需要に合わせて再配置します。
            </p>

            <div className="space-y-6">
              {/* ① 重い処方は"谷"に寄せる */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-green-500">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">①</span>
                  重い処方は"谷"に寄せる
                </h3>
                <ul className="space-y-2 mb-4 ml-13">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-checkbox-circle-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>一包化</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-checkbox-circle-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>施設・在宅の分包</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-checkbox-circle-line text-green-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>時間のかかる散剤</span>
                  </li>
                </ul>
                <div className="bg-green-50 p-4 rounded-lg ml-13">
                  <p className="text-gray-800 font-medium">
                    👉 需要が少ない時間帯に集中的に処理。
                  </p>
                </div>
              </div>

              {/* ② 投薬人員は"ピーク"に厚く */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-red-500">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">②</span>
                  投薬人員は"ピーク"に厚く
                </h3>
                <ul className="space-y-2 mb-4 ml-13">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-checkbox-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>調剤は後ろ倒しでもよい</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-checkbox-circle-line text-red-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span>投薬・鑑査はピークに集中配置</span>
                  </li>
                </ul>
                <div className="bg-red-50 p-4 rounded-lg ml-13">
                  <p className="text-gray-800 font-medium">
                    👉 これは <Link to="/theory/toc" className="text-red-600 hover:text-red-700 underline">TOC</Link>でいう制約強化です。
                  </p>
                </div>
              </div>

              {/* ③ 午前は「流れ重視」、午後は「整備重視」 */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-purple-500">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0">③</span>
                  午前は「流れ重視」、午後は「整備重視」
                </h3>
                <ul className="space-y-2 mb-4 ml-13">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-sun-line text-orange-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span><strong>午前：</strong>止めない・詰まらせない</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-moon-line text-purple-500 mr-2 mt-1 flex-shrink-0"></i>
                    <span><strong>午後：</strong>在庫整理・記録・教育・重作業</span>
                  </li>
                </ul>
                <div className="bg-purple-50 p-4 rounded-lg ml-13">
                  <p className="text-gray-800 font-medium">
                    👉 同じ1日でも、目的を切り替える。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ヤーキーズ・ドットソンとの関係 */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">ヤーキーズ・ドットソンとの関係</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              タクト思考は、ヤーキーズ・ドットソンの法則（緊張とパフォーマンス）と完全に噛み合います。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <i className="ri-arrow-up-circle-fill text-3xl text-red-500 mr-3"></i>
                  <h3 className="text-xl font-bold text-red-700">ピーク</h3>
                </div>
                <p className="text-gray-800">
                  緊張が高すぎる → <strong className="text-red-700">事故リスク</strong>
                </p>
              </div>

              <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <i className="ri-arrow-down-circle-fill text-3xl text-blue-500 mr-3"></i>
                  <h3 className="text-xl font-bold text-blue-700">谷</h3>
                </div>
                <p className="text-gray-800">
                  緊張が低すぎる → <strong className="text-blue-700">ケアレスミス</strong>
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-xl text-center">
              <p className="text-xl font-bold">
                👉 仕事を時間帯で分けることで、緊張レベルを"最適ゾーン"に戻す。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">KPI｜タクトが合っているかを見る指標</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              難しい数値はいりません。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-purple-500">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-time-line text-2xl text-purple-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">時間帯別の受付枚数（λ）</h3>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-pink-500">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-hourglass-line text-2xl text-pink-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">時間帯別の待ち時間</h3>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-red-500">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-alert-line text-2xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">ピーク時間帯のインシデント密度</h3>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-blue-500">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-error-warning-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">谷時間帯の単純ミス件数</h3>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-xl">
              <p className="text-lg text-gray-800 font-medium">
                これを見るだけで、リズムのズレが分かります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PharmaBeast的に */}
      <section className="py-16 bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start mb-6">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                <i className="ri-bear-smile-line text-3xl text-teal-600"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-4">PharmaBeast的に</h2>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    シフト調整、人員配置がその店舗に合っているのかをマネジメントする。
                  </p>
                  <p>
                    パート職員、時短勤務職員の適正配置がポイント
                  </p>
                  <p className="font-bold text-xl">
                    ルールを決めて、人事異動を実施
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 明日の一手 */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">明日の一手｜タクト思考の第一歩</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              明日やることは、これだけです。
            </p>

            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl p-10 shadow-xl mb-8">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mr-6 flex-shrink-0">
                  <i className="ri-flag-line text-3xl text-purple-500"></i>
                </div>
                <div>
                  <p className="text-2xl font-bold mb-4">
                    👉 「1日の中で一番混む1時間」を全員で共有する。
                  </p>
                  <p className="text-lg text-white/90">
                    そしてその時間帯に、
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
                <div className="flex items-center mb-3">
                  <i className="ri-question-line text-2xl text-orange-500 mr-3"></i>
                  <h3 className="text-xl font-bold text-gray-900">一包化を入れていないか</h3>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
                <div className="flex items-center mb-3">
                  <i className="ri-question-line text-2xl text-orange-500 mr-3"></i>
                  <h3 className="text-xl font-bold text-gray-900">投薬人員は足りているか</h3>
                </div>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              を見直す。
            </p>

            <div className="mt-8 bg-gradient-to-r from-green-500 to-teal-500 text-white p-6 rounded-xl text-center">
              <p className="text-xl font-bold">
                それだけで、現場の空気が変わり始めます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 関連理論 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">関連する理論</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link 
                to="/theory/toc" 
                className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <i className="ri-link-m text-3xl text-blue-600 mb-3"></i>
                <h3 className="text-xl font-bold text-gray-900 mb-2">TOC（制約理論）</h3>
                <p className="text-gray-600">制約を見つける</p>
              </Link>

              <Link 
                to="/theory/dbr" 
                className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <i className="ri-link-m text-3xl text-green-600 mb-3"></i>
                <h3 className="text-xl font-bold text-gray-900 mb-2">DBR</h3>
                <p className="text-gray-600">入れすぎを止める</p>
              </Link>

              <Link 
                to="/theory" 
                className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <i className="ri-arrow-left-line text-3xl text-purple-600 mb-3"></i>
                <h3 className="text-xl font-bold text-gray-900 mb-2">理論一覧</h3>
                <p className="text-gray-600">すべての理論を見る</p>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
